import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  ArrowForward,
  ErrorOutline,
  VisibilityOffOutlined,
  WarningAmber,
} from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';
import type { InspectionAlert } from '@/helpers/inspectionAlerts';

const alertStyleMap = {
  OVERDUE: {
    icon: ErrorOutline,
    iconColor: 'error.main',
    borderColor: (theme: Theme) => alpha(theme.palette.error.main, 0.35),
    background: (theme: Theme) => alpha(theme.palette.error.main, 0.05),
    titleColor: 'error.dark',
  },
  DUE_SOON: {
    icon: WarningAmber,
    iconColor: 'warning.dark',
    borderColor: (theme: Theme) => alpha(theme.palette.warning.main, 0.35),
    background: (theme: Theme) => alpha(theme.palette.warning.main, 0.08),
    titleColor: 'warning.dark',
  },
} as const;

interface InspectionAlertCardProps {
  alert: InspectionAlert;
  showBuildingName?: boolean;
  onDismiss?: (alert: InspectionAlert) => void;
  dismissDisabled?: boolean;
}

const InspectionAlertCard = ({
  alert,
  showBuildingName = false,
  onDismiss,
  dismissDisabled = false,
}: InspectionAlertCardProps) => {
  const styles = alertStyleMap[alert.alertType];
  const Icon = styles.icon;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: styles.borderColor,
        background: styles.background,
        borderRadius: 3,
        p: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Icon sx={{ color: styles.iconColor, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Typography variant="subtitle2" sx={{ color: styles.titleColor, fontWeight: 700, pr: 1 }}>
              {alert.title}
            </Typography>
            {onDismiss && (
              <IconButton
                size="small"
                disabled={dismissDisabled}
                onClick={() => onDismiss(alert)}
                sx={{ mt: -0.5, mr: -0.75 }}
                title="Skrýt do zítřka"
              >
                <VisibilityOffOutlined fontSize="small" />
              </IconButton>
            )}
          </Stack>

          {showBuildingName && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              Objekt: {alert.buildingName}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            {alert.message}
          </Typography>

          <Button
            component={RouterLink}
            to={`${alert.actionPath}?section=inspections`}
            size="small"
            endIcon={<ArrowForward fontSize="small" />}
            sx={{ px: 0, minWidth: 0, textTransform: 'none', fontWeight: 700 }}
          >
            Zobrazit revizi a dokumenty
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default InspectionAlertCard;
